import React, { useState } from 'react';
import { BookOpen, ChevronDown, ChevronUp, FileText, Link as LinkIcon } from 'lucide-react';
import { NatureCard } from '../shared/NatureCard';
import { BotanicalDivider } from '../shared/BotanicalDivider';
import './ReferencesTab.css';

interface ReferenceCategory {
  title: string;
  icon: string;
  sections: ReferenceSection[];
}

interface ReferenceSection {
  title: string;
  formulas?: Formula[];
  references?: Reference[];
  notes?: string[];
}

interface Formula {
  name: string;
  equation: string;
  variables: { symbol: string; description: string }[];
  example?: string;
}

interface Reference {
  authors: string;
  year: number;
  title: string;
  journal?: string;
  organization?: string;
  url?: string;
}

export const ReferencesTab: React.FC = () => {
  const [expandedCategories, setExpandedCategories] = useState<Set<number>>(new Set([0]));

  const toggleCategory = (index: number) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(index)) {
      newExpanded.delete(index);
    } else {
      newExpanded.add(index);
    }
    setExpandedCategories(newExpanded);
  };

  const categories: ReferenceCategory[] = [
    {
      title: 'Oxygen Production',
      icon: '🫁',
      sections: [
        {
          title: 'Core Formula',
          formulas: [
            {
              name: 'Annual Oxygen Production',
              equation: 'O₂ (kg/year) = 100 kg/tree/year',
              variables: [
                { symbol: 'O₂', description: 'Oxygen produced in kilograms per year' },
                { symbol: 'tree', description: 'Individual mature tree' },
              ],
              example: 'For 100 trees: 100 × 100 = 10,000 kg O₂/year',
            },
            {
              name: 'People Supported',
              equation: 'People = Total O₂ (kg) / 840 kg',
              variables: [
                { symbol: 'People', description: 'Number of people supported annually' },
                { symbol: '840 kg', description: 'Annual O₂ requirement per person' },
              ],
              example: 'For 10,000 kg O₂: 10,000 / 840 ≈ 12 people',
            },
            {
              name: 'Medical Cylinders Equivalent',
              equation: 'Cylinders = Total O₂ (liters) / 7,000 L',
              variables: [
                { symbol: 'Cylinders', description: 'Number of medical oxygen cylinders' },
                { symbol: '7,000 L', description: 'Volume of one medical cylinder' },
              ],
            },
          ],
          references: [
            {
              authors: 'McPherson, E.G., et al.',
              year: 2011,
              title: 'Million trees Los Angeles canopy cover and benefit assessment',
              journal: 'Landscape and Urban Planning',
              url: 'https://doi.org/10.1016/j.landurbplan.2010.10.011',
            },
            {
              authors: 'Nowak, D.J., et al.',
              year: 2007,
              title: 'Oxygen production by urban trees in the United States',
              journal: 'Arboriculture & Urban Forestry',
            },
          ],
          notes: [
            'The 100 kg/tree/year estimate is based on average mature deciduous trees in urban settings',
            'Actual rates vary by species, age, health, and environmental conditions',
            'This is a conservative estimate; some large trees can produce 150-200 kg O₂/year',
          ],
        },
      ],
    },
    {
      title: 'Carbon Sequestration',
      icon: '🌍',
      sections: [
        {
          title: 'Biomass & Carbon Storage',
          formulas: [
            {
              name: 'Carbon Stored',
              equation: 'C (kg) = 0.5 × Total Biomass',
              variables: [
                { symbol: 'C', description: 'Carbon stored in kilograms' },
                { symbol: 'Biomass', description: 'Total tree biomass (above + below ground)' },
                { symbol: '0.5', description: 'Carbon fraction (50% of dry biomass)' },
              ],
            },
            {
              name: 'CO₂ Equivalent',
              equation: 'CO₂ (kg) = C × 3.67',
              variables: [
                { symbol: 'CO₂', description: 'Carbon dioxide equivalent' },
                { symbol: 'C', description: 'Carbon in kilograms' },
                { symbol: '3.67', description: 'Molecular weight ratio (44/12)' },
              ],
            },
            {
              name: 'Vehicle Kilometers Offset',
              equation: 'km = CO₂ (kg) / 0.12',
              variables: [
                { symbol: 'km', description: 'Vehicle kilometers offset' },
                { symbol: '0.12', description: 'Average vehicle emissions (kg CO₂/km)' },
              ],
            },
          ],
          references: [
            {
              authors: 'IPCC',
              year: 2006,
              title: 'Guidelines for National Greenhouse Gas Inventories',
              organization: 'Intergovernmental Panel on Climate Change',
            },
            {
              authors: 'Chave, J., et al.',
              year: 2014,
              title: 'Improved allometric models to estimate the aboveground biomass of tropical trees',
              journal: 'Global Change Biology',
              url: 'https://doi.org/10.1111/gcb.12629',
            },
          ],
        },
        {
          title: 'Market Valuation',
          formulas: [
            {
              name: 'Carbon Market Value',
              equation: 'Value (₹) = (CO₂ tonnes × €90) × 88.5',
              variables: [
                { symbol: 'CO₂ tonnes', description: 'Total CO₂ sequestered in metric tonnes' },
                { symbol: '€90', description: 'EU ETS carbon price per tonne (2024)' },
                { symbol: '88.5', description: 'EUR to INR conversion rate' },
              ],
            },
          ],
          references: [
            {
              authors: 'European Commission',
              year: 2024,
              title: 'EU Emissions Trading System (EU ETS)',
              organization: 'European Union',
              url: 'https://ec.europa.eu/clima/policies/ets_en',
            },
          ],
        },
      ],
    },
    {
      title: 'Air Quality & Pollutant Removal',
      icon: '💨',
      sections: [
        {
          title: 'Pollutant Removal Rates',
          formulas: [
            {
              name: 'Total Pollutant Removal',
              equation: 'Pollutants (kg/year) = 1.46 × Number of Trees',
              variables: [
                { symbol: '1.46 kg', description: 'Average pollutant removal per tree per year' },
              ],
            },
            {
              name: 'Breakdown by Pollutant Type',
              equation: 'PM (45%), NO₂ (25%), O₃ (15%), SO₂ (10%), CO (5%)',
              variables: [
                { symbol: 'PM', description: 'Particulate Matter' },
                { symbol: 'NO₂', description: 'Nitrogen Dioxide' },
                { symbol: 'O₃', description: 'Ozone' },
                { symbol: 'SO₂', description: 'Sulfur Dioxide' },
                { symbol: 'CO', description: 'Carbon Monoxide' },
              ],
            },
          ],
          references: [
            {
              authors: 'Nowak, D.J., et al.',
              year: 2006,
              title: 'Air pollution removal by urban trees and shrubs in the United States',
              journal: 'Urban Forestry & Urban Greening',
              url: 'https://doi.org/10.1016/j.ufug.2006.01.007',
            },
            {
              authors: 'Heisler, G.M., & Brazel, A.J.',
              year: 2010,
              title: 'The urban physical environment: Temperature and urban heat islands',
              journal: 'Urban Ecosystem Ecology',
            },
          ],
          notes: [
            'Removal rates vary significantly by species, leaf area, and local air quality',
            'Broadleaf deciduous trees generally remove more pollutants than conifers',
            'Peak removal occurs during growing season when leaves are present',
          ],
        },
      ],
    },
    {
      title: 'Biodiversity Indices',
      icon: '🦋',
      sections: [
        {
          title: 'Shannon Diversity Index',
          formulas: [
            {
              name: "Shannon Index (H')",
              equation: "H' = -Σ(pᵢ × ln(pᵢ))",
              variables: [
                { symbol: 'pᵢ', description: 'Proportion of individuals belonging to species i' },
                { symbol: 'ln', description: 'Natural logarithm' },
                { symbol: 'Σ', description: 'Sum across all species' },
              ],
              example: 'Range: 0 (no diversity) to 4+ (high diversity). Values >3 indicate healthy diversity.',
            },
          ],
        },
        {
          title: "Simpson's Index",
          formulas: [
            {
              name: "Simpson's Diversity Index (D)",
              equation: 'D = 1 - Σ(pᵢ²)',
              variables: [
                { symbol: 'D', description: 'Probability that two randomly selected individuals are different species' },
                { symbol: 'pᵢ', description: 'Proportion of species i' },
              ],
              example: 'Range: 0 (no diversity) to 1 (infinite diversity)',
            },
          ],
        },
        {
          title: 'Evenness Index',
          formulas: [
            {
              name: "Pielou's Evenness (J')",
              equation: "J' = H' / ln(S)",
              variables: [
                { symbol: "H'", description: 'Shannon diversity index' },
                { symbol: 'S', description: 'Total number of species (richness)' },
              ],
              example: 'Range: 0 to 1. Values close to 1 indicate species are evenly distributed.',
            },
          ],
          references: [
            {
              authors: 'Shannon, C.E.',
              year: 1948,
              title: 'A mathematical theory of communication',
              journal: 'Bell System Technical Journal',
            },
            {
              authors: 'Simpson, E.H.',
              year: 1949,
              title: 'Measurement of diversity',
              journal: 'Nature',
            },
            {
              authors: 'Pielou, E.C.',
              year: 1966,
              title: 'The measurement of diversity in different types of biological collections',
              journal: 'Journal of Theoretical Biology',
            },
          ],
        },
      ],
    },
    {
      title: 'Ecosystem Services',
      icon: '⚡',
      sections: [
        {
          title: 'Cooling Services',
          formulas: [
            {
              name: 'Room Cooling Capacity',
              equation: 'Rooms = Trees × 10',
              variables: [
                { symbol: 'Rooms', description: 'Room cooling equivalents' },
                { symbol: '10', description: 'One mature tree ≈ 10 air conditioning units' },
              ],
            },
            {
              name: 'Energy Savings',
              equation: 'kWh = Trees × 100 kWh/year',
              variables: [
                { symbol: 'kWh', description: 'Kilowatt-hours saved annually' },
                { symbol: '100', description: 'Average energy savings per tree' },
              ],
            },
          ],
          references: [
            {
              authors: 'Ecobal EU',
              year: 2018,
              title: 'Tree cooling equivalent to air conditioning units',
              organization: 'Ecobal Environmental Services',
            },
          ],
        },
        {
          title: 'Stormwater Management',
          formulas: [
            {
              name: 'Rainfall Interception',
              equation: 'Liters = Trees × 6,000 L/year',
              variables: [
                { symbol: 'Liters', description: 'Rainfall intercepted annually' },
                { symbol: '6,000', description: 'Average interception per tree' },
              ],
            },
          ],
          references: [
            {
              authors: 'McPherson, E.G., & Xiao, Q.',
              year: 2004,
              title: 'Rainfall interception by urban trees',
              journal: 'Journal of Arboriculture',
            },
          ],
        },
      ],
    },
    {
      title: 'Data Quality & Methodology',
      icon: '📊',
      sections: [
        {
          title: 'Data Collection',
          notes: [
            'All tree data collected through field surveys using standardized protocols',
            'GBH (Girth at Breast Height) measured at 1.3m from ground level',
            'GPS coordinates recorded using high-precision equipment (±3m accuracy)',
            'Species identification verified by botanical experts',
            'Last updated: November 2024',
          ],
        },
        {
          title: 'Assumptions & Limitations',
          notes: [
            'All calculations assume healthy, mature trees unless otherwise specified',
            'Environmental benefits vary with seasonal changes and tree health status',
            'CO₂ sequestration rates are annual estimates; actual uptake varies daily',
            'Economic valuations use 2024 market rates and may fluctuate',
            'Oxygen production assumes standard atmospheric conditions',
            'Pollutant removal rates based on temperate climate studies; Indian climate adjustments needed',
          ],
        },
        {
          title: 'Confidence Intervals',
          notes: [
            'Oxygen production: ±15% (80-120 kg/tree/year)',
            'Carbon sequestration: ±20% based on biomass estimation uncertainties',
            'Air quality: ±25% due to variable atmospheric conditions',
            'Biodiversity indices: High confidence (±5%) due to complete census data',
          ],
        },
      ],
    },
  ];

  return (
    <section className="references-tab">
      <div className="references-tab__container">
        {/* Header */}
        <div className="references-tab__header">
          <BookOpen size={48} className="references-tab__header-icon" />
          <div>
            <h2 className="references-tab__title">Scientific References & Methodology</h2>
            <p className="references-tab__subtitle">
              Comprehensive documentation of formulas, calculations, data sources, and peer-reviewed references
            </p>
          </div>
        </div>

        <BotanicalDivider style="simple" spacing="medium" />

        {/* Introduction */}
        <NatureCard className="references-tab__intro">
          <h3 className="references-tab__intro-title">About This Section</h3>
          <p className="references-tab__intro-text">
            This comprehensive reference section provides complete transparency into all calculations, formulas,
            and data sources used throughout the Empress Botanical Garden Dashboard. Every metric displayed has been derived
            using peer-reviewed scientific methodologies and is documented here for verification and educational purposes.
          </p>
          <div className="references-tab__intro-note">
            <FileText size={20} />
            <span>
              All formulas and references are cross-referenced with published scientific literature.
              Click on each category below to explore the detailed methodology.
            </span>
          </div>
        </NatureCard>

        {/* Categories */}
        <div className="references-tab__categories">
          {categories.map((category, catIndex) => (
            <NatureCard key={catIndex} className="references-tab__category" delay={catIndex * 0.05}>
              <div
                className="references-tab__category-header"
                onClick={() => toggleCategory(catIndex)}
              >
                <div className="references-tab__category-header-content">
                  <span className="references-tab__category-icon">{category.icon}</span>
                  <h3 className="references-tab__category-title">{category.title}</h3>
                </div>
                <button className="references-tab__category-toggle">
                  {expandedCategories.has(catIndex) ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                </button>
              </div>

              {expandedCategories.has(catIndex) && (
                <div className="references-tab__category-content">
                  {category.sections.map((section, secIndex) => (
                    <div key={secIndex} className="references-tab__section">
                      <h4 className="references-tab__section-title">{section.title}</h4>

                      {/* Formulas */}
                      {section.formulas && section.formulas.length > 0 && (
                        <div className="references-tab__formulas">
                          {section.formulas.map((formula, formIndex) => (
                            <div key={formIndex} className="references-tab__formula">
                              <div className="references-tab__formula-name">{formula.name}</div>
                              <div className="references-tab__formula-equation">{formula.equation}</div>
                              <div className="references-tab__formula-variables">
                                <strong>Where:</strong>
                                <ul>
                                  {formula.variables.map((v, vIndex) => (
                                    <li key={vIndex}>
                                      <strong>{v.symbol}</strong> = {v.description}
                                    </li>
                                  ))}
                                </ul>
                              </div>
                              {formula.example && (
                                <div className="references-tab__formula-example">
                                  <strong>Example:</strong> {formula.example}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* References */}
                      {section.references && section.references.length > 0 && (
                        <div className="references-tab__refs">
                          <div className="references-tab__refs-title">
                            <LinkIcon size={16} />
                            <span>References</span>
                          </div>
                          {section.references.map((ref, refIndex) => (
                            <div key={refIndex} className="references-tab__ref">
                              <div className="references-tab__ref-citation">
                                {ref.authors} ({ref.year}). <em>{ref.title}</em>.
                                {ref.journal && <span> {ref.journal}.</span>}
                                {ref.organization && <span> {ref.organization}.</span>}
                              </div>
                              {ref.url && (
                                <a
                                  href={ref.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="references-tab__ref-link"
                                >
                                  View Source →
                                </a>
                              )}
                            </div>
                          ))}
                        </div>
                      )}

                      {/* Notes */}
                      {section.notes && section.notes.length > 0 && (
                        <div className="references-tab__notes">
                          <div className="references-tab__notes-title">📝 Notes</div>
                          <ul>
                            {section.notes.map((note, noteIndex) => (
                              <li key={noteIndex}>{note}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </NatureCard>
          ))}
        </div>

        {/* Footer Note */}
        <div className="references-tab__footer">
          <p>
            <strong>Citation:</strong> This dashboard and its methodologies are maintained by The Green Connect.
            For academic citations or data requests, please contact the project administrators.
          </p>
          <p className="references-tab__footer-disclaimer">
            All calculations are based on established scientific literature and represent best estimates given available data.
            Actual environmental benefits may vary based on local conditions, tree health, and seasonal factors.
          </p>
        </div>
      </div>
    </section>
  );
};

export default ReferencesTab;
